import { AlertSeverity, AlertWebhook, AlertWebhookFormat, AlertWebhooks } from "@app/shared";
import { endpointApiDocsPath } from "@app/shared/src/components/Integrations/EndpointApiDocs/paths";
import { FC } from "react";
import { useHref } from "react-router-dom";
import { toastError, toastSuccess } from "../../../helpers";
import { useConfirmDialog, useEditModelDialog, useFetch } from "../../../hooks";
import { HoverTooltip } from "../../shared/HoverTooltip";
import { IconLink, Icons, PageContainer, SectionIntro, SortableTable, StatusDot } from "../../shared";
import { DropDownMenu } from "../../shared/DropDownMenu";
import { CheckboxInput, EnumSelect, Input, InputRow, Select } from "../../shared/inputs";
import { enumLabel, minSeverityLabel, minSeverityOptions, zonedTime } from "./alertDisplay";

type AlertWebhookTableProps = {
    organizationId: string;
};

// Org-level webhook destinations (Slack, Teams, or generic JSON). Generic payloads are
// signed with HMAC-SHA256 in the X-Omnisight-Signature header using the signing secret
// below, which the server provisions on create.
export const AlertWebhookTable: FC<AlertWebhookTableProps> = ({ organizationId }) => {
    const [webhooks, , webhookHelpers] = useFetch(() => AlertWebhooks.list(organizationId), [organizationId]);
    // Basename-aware so the link still resolves behind the device proxy, same as the client-endpoint
    // docs link. Imported from paths (not the barrel) so linking doesn't pull the OpenAPI viewer and the
    // embedded specs into this page's bundle.
    const docsHref = `${useHref("/").replace(/\/$/, "")}${endpointApiDocsPath("alert-webhook")}`;
    const webhookDialog = useEditModelDialog(AlertWebhook);
    const confirmDialog = useConfirmDialog();

    // Deliberately un-caught: a rejected save (bad URL, missing name) has to land in the
    // dialog's error summary rather than leaving the Save button looking dead.
    const saveWebhook = async (toSave: Partial<AlertWebhook>) => {
        if (toSave.id) await AlertWebhooks.update(toSave.id, toSave as AlertWebhook);
        else await AlertWebhooks.create(toSave as AlertWebhook);

        webhookHelpers.refreshData();
        toastSuccess("Webhook Saved", "Use Send Test to confirm the destination accepts alerts.");
    };

    // class-validator's MaxLength FAILS on undefined, and the TS exporter emits @MaxLength
    // for [MaxLength] on a nullable string without an accompanying @IsOptional(). Left
    // undefined, signingSecret/lastFailureMessage fail validation and Save shows a bare
    // "correct the following errors" with nothing to correct — neither field has an input
    // bound to it on the add form, so there is nowhere to put an inline message. Empty
    // strings satisfy MaxLength; @IsNotEmpty still guards name and url. An empty secret also
    // means "provision one" on create and "keep the current one" on update, server-side.
    const validatable = (webhook: Partial<AlertWebhook>): Partial<AlertWebhook> => ({
        ...webhook,
        signingSecret: webhook.signingSecret ?? "",
        lastFailureMessage: webhook.lastFailureMessage ?? "",
    });

    const handleAddOnClick = () => {
        webhookDialog.show(
            saveWebhook,
            "Add Webhook",
            validatable({
                organizationId,
                format: AlertWebhookFormat.Auto,
                minSeverity: AlertSeverity.Warning,
                isActive: true,
            })
        );
    };

    const handleEditOnClick = (toEdit: AlertWebhook | undefined) => {
        if (!toEdit) return;
        webhookDialog.show(saveWebhook, "Edit Webhook", validatable(toEdit));
    };

    const handleSendTestOnClick = async (toTest: AlertWebhook | undefined) => {
        if (!toTest) return;
        try {
            const result = await AlertWebhooks.test(toTest.id);
            webhookHelpers.refreshData();
            // The server explains the failure (rejected status, refused destination, socket error);
            // showing its message beats a generic "check everything" guess.
            if (result?.success) toastSuccess("Test Alert Sent", result.message ?? `${toTest.name} accepted the test payload.`);
            else toastError("Test Alert Failed", result?.message ?? "The destination did not accept the test alert.");
        } catch (e) {
            toastError("Test Alert Failed", e + "");
        }
    };

    const handleDeleteOnClick = (toDelete: AlertWebhook | undefined) => {
        if (!toDelete) return;
        confirmDialog.show("Delete Webhook?", `Alerts will no longer be delivered to ${toDelete.name}.`, "Delete", async () => {
            try {
                await AlertWebhooks.deleteWebhook(toDelete.id);
                webhookHelpers.refreshData();
                toastSuccess("Webhook Deleted");
            } catch (e) {
                toastError("Error Deleting Webhook", e + "");
            }
        });
    };

    const deliveryStatus = (webhook: AlertWebhook | undefined) => {
        if (!webhook) return undefined;
        if (!webhook.isActive) return "error" as const;
        if (webhook.consecutiveFailureCount > 0) return "warn" as const;
        return webhook.lastSuccessAtUtc ? ("ok" as const) : undefined;
    };

    const deliveryHover = (webhook: AlertWebhook | undefined) => {
        if (!webhook) return undefined;
        if (!webhook.isActive) return `Disabled${webhook.consecutiveFailureCount > 0 ? ` after ${webhook.consecutiveFailureCount} consecutive failure(s)` : ""}`;
        if (webhook.consecutiveFailureCount > 0) return `${webhook.consecutiveFailureCount} consecutive failure(s) — last failed ${zonedTime(webhook.lastFailureAtUtc)}${webhook.lastFailureMessage ? `: ${webhook.lastFailureMessage}` : ""}`;
        return webhook.lastSuccessAtUtc ? `Last delivered ${zonedTime(webhook.lastSuccessAtUtc)}` : "No deliveries yet";
    };

    return (
        <>
            <PageContainer
                Icon={Icons.Api}
                title="Webhook Destinations"
                titleRight={
                    <div className="flex justify-center items-center gap-4">
                        <a
                            href={docsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-link-page hover:text-link-page-hover underline whitespace-nowrap inline-flex items-center gap-1"
                            title="Payload schema, headers, signature verification, retry semantics, and a downloadable OpenAPI spec"
                        >
                            <Icons.Info size={12} /> API Docs
                        </a>
                        <IconLink Icon={Icons.PlusCircle} onClick={handleAddOnClick}>
                            Add New
                        </IconLink>
                    </div>
                }
            >
                <SectionIntro title="Send alerts to another system instead of to a person.">
                    <p>
                        Paste an incoming-webhook URL from <strong>Slack</strong> or <strong>Microsoft Teams</strong> and we'll
                        post formatted messages to that channel — the format is detected from the URL, so Auto-detect is
                        normally the right choice. Any other destination receives JSON.
                    </p>
                    <p>
                        For a system of your own, hand your developer the <strong>API Docs</strong> link above — it has the full
                        payload schema, the headers, how to verify the signature, the retry behaviour, and a downloadable
                        OpenAPI spec. They'll also need the signing secret shown when you edit a webhook.
                    </p>
                    <p>
                        Use <strong>Send Test</strong> to confirm a destination works before relying on it. Repeated failures
                        switch a webhook off automatically — the status dot tells you why — and internal or private network
                        addresses are refused unless an administrator has allowed them.
                    </p>
                </SectionIntro>
                <SortableTable
                    initialSortKey="name"
                    isLoading={webhookHelpers.isLoading}
                    emptyValue={<p className="m-5">No Webhook Destinations Found</p>}
                    columns={[
                        {
                            header: "Status",
                            width: 50,
                            dataKey: "isActive",
                            renderFunc: (w) => (
                                <div className="flex items-center h-full">
                                    <StatusDot status={deliveryStatus(w)} sizeClasses="w-3 h-3" hoverMessage={deliveryHover(w)} />
                                </div>
                            ),
                        },
                        {
                            header: "Name",
                            width: 180,
                            dataKey: "name",
                            filterable: true,
                            filterType: "text",
                        },
                        {
                            header: "URL",
                            width: 300,
                            dataKey: "url",
                            filterable: true,
                            filterType: "text",
                            renderFunc: (w) => (
                                <HoverTooltip hoverable={<span className="font-mono text-xs truncate block">{w?.url}</span>}>
                                    <span className="text-xs break-all">{w?.url}</span>
                                </HoverTooltip>
                            ),
                        },
                        {
                            header: "Format",
                            width: 120,
                            dataKey: "format",
                            filterable: true,
                            filterType: "select",
                            filterOptions: [
                                { value: AlertWebhookFormat.Auto.toString(), label: "Auto" },
                                { value: AlertWebhookFormat.Generic.toString(), label: "Generic" },
                                { value: AlertWebhookFormat.Slack.toString(), label: "Slack" },
                                { value: AlertWebhookFormat.Teams.toString(), label: "Teams" },
                            ],
                            renderFunc: (w) => (w ? enumLabel(AlertWebhookFormat[w.format] ?? "") : ""),
                        },
                        {
                            header: "Notify About",
                            width: 150,
                            dataKey: "minSeverity",
                            renderFunc: (w) => minSeverityLabel(w?.minSeverity),
                        },
                        {
                            header: "Last Delivery",
                            width: 180,
                            dataKey: "lastSuccessAtUtc",
                            renderFunc: (w) => zonedTime(w?.lastSuccessAtUtc),
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (w) => (
                                <DropDownMenu>
                                    <IconLink Icon={Icons.Play} onClick={() => handleSendTestOnClick(w)}>
                                        Send Test
                                    </IconLink>
                                    <IconLink Icon={Icons.Edit} onClick={() => handleEditOnClick(w)}>
                                        Edit
                                    </IconLink>
                                    <IconLink Icon={Icons.Trash} onClick={() => handleDeleteOnClick(w)}>
                                        Delete
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={webhooks}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {webhookDialog.renderDialog((model, helpers) => (
                <>
                    <InputRow>
                        <Input label="Name" placeholder="On-call Slack channel" {...helpers.bindingsFor("name")} />
                    </InputRow>
                    <InputRow>
                        <Input label="URL" placeholder="https://hooks.slack.com/services/..." {...helpers.bindingsFor("url")} />
                        <div className="text-gray-500 text-sm">Must be an absolute http(s) URL.</div>
                    </InputRow>
                    <InputRow>
                        <EnumSelect
                            label="Payload Format"
                            enumType={AlertWebhookFormat}
                            nameToDisplayConverter={(key) => (key === "Auto" ? "Auto-detect from URL" : enumLabel(key))}
                            value={model.format}
                            onChange={(value) => helpers.updateModel("format", (value ?? AlertWebhookFormat.Auto) as AlertWebhookFormat)}
                        />
                    </InputRow>
                    <InputRow>
                        <Select
                            label="Minimum Severity"
                            options={minSeverityOptions}
                            value={model.minSeverity}
                            onChange={(e) => helpers.updateModel("minSeverity", Number(e.target.value) as AlertSeverity)}
                        />
                    </InputRow>
                    {model.id && (
                        <>
                            <InputRow>
                                <Input label="Signing Secret" {...helpers.bindingsFor("signingSecret")} />
                                <div className="text-gray-500 text-sm">
                                    Generic payloads are signed with HMAC-SHA256 in the <code>X-Omnisight-Signature</code> header. Clearing this field keeps the current secret.
                                </div>
                            </InputRow>
                            <InputRow>
                                <CheckboxInput
                                    label="Active"
                                    checked={model.isActive ?? false}
                                    onChange={(e) => helpers.updateModel("isActive", e.target.checked)}
                                />
                                <div className="text-gray-500 text-sm">Repeated delivery failures disable a webhook automatically; re-check this to resume delivery.</div>
                            </InputRow>
                        </>
                    )}
                </>
            ))}
        </>
    );
};
